import React from 'react';

import { DateTime, Duration, Interval } from 'luxon';

import SelectionTextBox from './SelectionTextBox';
import {SelectionInterval} from '../utils/SelectionInterval';
import RadioSelector from './RadioSelector';
import { TemporalDisplayOpt } from './Constants';

import './TemporalWidget.css';

interface TemporalWidgetProps {
    interval: SelectionInterval
    example: string;
    onintervalchange: (intv: SelectionInterval, recenter_timeline?: boolean, clear_infobox?: boolean) => void;
    data_limits: Interval
    cday_offset: Duration
    window_size: {width: number, height: number}
    random_day_func: (() => Promise<DateTime>) | null;
    env_intv_dur_cutoff: Duration
    temp_display: TemporalDisplayOpt;
    ontempdisplaychange: (display: TemporalDisplayOpt) => void;
}

type SearchActionType = 'search' | 'remove' | 'random' | null;

interface TemporalWidgetState {
    num_reminder_steps: number;
    action: SearchActionType;
}

class TemporalWidget extends React.Component<TemporalWidgetProps, TemporalWidgetState> {
    constructor(props: TemporalWidgetProps) {
        super(props);
        
        this.state = {
            'num_reminder_steps': 0,
            'action': null,
        }

        this.updateTemporalSearch = this.updateTemporalSearch.bind(this);
        this.parseDateStr = this.parseDateStr.bind(this);
        this.viewFullDataset = this.viewFullDataset.bind(this);
        this.pickRandomDay = this.pickRandomDay.bind(this);
    }

    parseDateStr(date_str: string) {
        return SelectionInterval.fromString(date_str, this.props['cday_offset'], this.props['data_limits']);
    }

    updateTemporalSearch(intvs: SelectionInterval[]) {
        if (intvs.length == 0) {
            this.setState({'num_reminder_steps': this.state['num_reminder_steps'] + 1, 'action': 'remove'})
            this.viewFullDataset();
        }
        else {
            const new_n_steps = this.state['action'] == 'search' || this.state['action'] == 'random' ? 0 : this.state['num_reminder_steps'] + 1;
            this.setState({'num_reminder_steps': new_n_steps, 'action': 'search'});

            this.props['onintervalchange'](intvs[0], true);
        }
    }

    viewFullDataset() {
        const full_intv = new SelectionInterval(this.props['data_limits']);
        this.props['onintervalchange'](full_intv, true);
    }

    async pickRandomDay() {
        this.setState({'action': 'random'});
        
        if (this.props['random_day_func'] !== null) {
            const random_day = await this.props['random_day_func']();
            if (random_day !== undefined) {
                const new_intv = new SelectionInterval(Interval.after(random_day.plus(this.props['cday_offset']), {'hours': 24}));
                this.props['onintervalchange'](new_intv, true);
            }
        }
    }

    changeTempDisplay(display: string) {
        if (this.props['ontempdisplaychange']) {
            this.props['ontempdisplaychange'](display as TemporalDisplayOpt);
        }
    }

    render() {
        const intv = this.props['interval'];
        let display_intv: string[];

        if (intv.start().toMillis() == this.props['data_limits'].start.toMillis()
            && intv.end().toMillis() == this.props['data_limits'].end.toMillis()
            && !intv.isYearly()) {
            display_intv = [];
        }
        else {
            display_intv = [intv.toString(this.props['cday_offset'], this.props['data_limits'])];
        }

        const width = Math.min(500, this.props['window_size']['width'] * 0.9 - 200);

        const display_options = {
            'All': true, 
            'Hourly': this.props['interval'].duration() <= this.props['env_intv_dur_cutoff'], 
            'None': true,
        };

        const display_text = this.props['window_size']['width'] > 700 ? "Show:" : ''
        const random_text = this.props['window_size']['width'] > 700 ? "Surprise Me" : "Random"

        let prompt = "";
        if (this.state['num_reminder_steps'] >= 3) {
            prompt = "Try entering a date without pressing '\u00d7' first";
        }

        return (
            <div id='temporal-controls'>
                <div className='control' id='random-day'>
                    <button disabled={this.props['random_day_func'] === null} onClick={this.pickRandomDay}>{random_text}</button>
                </div>
                <div className='control'>
                    <SelectionTextBox 
                        content={display_intv}
                        placeholder={"e.g., " + this.props['example']}
                        onselectionupdate={this.updateTemporalSearch}
                        parser={this.parseDateStr}
                        prompt={prompt}
                        width={width}
                    />
                </div>
                <RadioSelector
                    label_text={display_text}
                    options={display_options}
                    selected={this.props['temp_display']}
                    onchange={this.changeTempDisplay.bind(this)}
                />
            </div>
        )
    }
}

export default TemporalWidget;
